import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import qrcodeService, { QRCodeCreateData } from '../services/qrcodeService';

const QRCodeCreate: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<QRCodeCreateData>({
    name: '',
    description: '',
    destination_url: '',
    shortcode: '',
    size: 400,
    color_dark: '#000000',
    color_light: '#FFFFFF',
    error_correction: 'M',
    margin: 4,
    module_style: 'square',
    logo_size: 20,
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
    if (error) setError(null);
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (error) setError(null);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const data: QRCodeCreateData = {
        ...formData,
        logo: logoFile || undefined,
      };

      await qrcodeService.create(data);
      setSuccess(true);

      // Redirect after short delay
      setTimeout(() => {
        navigate('/qrcodes');
      }, 1500);
    } catch (err: any) {
      setError(err.error || 'Failed to create QR code');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Alert severity="success">
            <Typography variant="h6">Code QR créé avec succès !</Typography>
            <Typography variant="body2">Redirection vers la liste des codes QR...</Typography>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/qrcodes')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Créer un nouveau code QR
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 4 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Informations de base
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Nom du code QR"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isLoading}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="URL de destination"
                  name="destination_url"
                  value={formData.destination_url}
                  onChange={handleChange}
                  disabled={isLoading}
                  type="url"
                  placeholder="https://example.com"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Code court personnalisé (optionnel)"
                  name="shortcode"
                  value={formData.shortcode}
                  onChange={handleChange}
                  disabled={isLoading}
                  helperText="Laisser vide pour générer automatiquement"
                />
              </Grid>

              {/* Customization */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Personnalisation
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Taille : {formData.size}px</Typography>
                <Slider
                  name="size"
                  value={formData.size}
                  onChange={(e, value) => setFormData({ ...formData, size: value as number })}
                  min={200}
                  max={1000}
                  step={50}
                  marks
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Marge : {formData.margin}</Typography>
                <Slider
                  name="margin"
                  value={formData.margin}
                  onChange={(e, value) => setFormData({ ...formData, margin: value as number })}
                  min={0}
                  max={10}
                  marks
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Couleur foncée"
                  name="color_dark"
                  type="color"
                  value={formData.color_dark}
                  onChange={handleChange}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: formData.color_dark,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Couleur claire"
                  name="color_light"
                  type="color"
                  value={formData.color_light}
                  onChange={handleChange}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: formData.color_light,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={isLoading}>
                  <InputLabel>Correction d'erreur</InputLabel>
                  <Select
                    name="error_correction"
                    value={formData.error_correction}
                    onChange={handleSelectChange}
                    label="Correction d'erreur"
                  >
                    <MenuItem value="L">Faible (7%)</MenuItem>
                    <MenuItem value="M">Moyen (15%)</MenuItem>
                    <MenuItem value="Q">Quartile (25%)</MenuItem>
                    <MenuItem value="H">Élevé (30%)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={isLoading}>
                  <InputLabel>Style des modules</InputLabel>
                  <Select
                    name="module_style"
                    value={formData.module_style}
                    onChange={handleSelectChange}
                    label="Style des modules"
                  >
                    <MenuItem value="square">Carré</MenuItem>
                    <MenuItem value="rounded">Arrondi</MenuItem>
                    <MenuItem value="dots">Points</MenuItem>
                    <MenuItem value="extra-rounded">Très arrondi</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Logo Upload */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Logo (Optionnel)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={isLoading}
                  fullWidth
                >
                  {logoFile ? 'Changer le logo' : 'Télécharger un logo'}
                  <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                </Button>

                {logoPreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        style={{ maxWidth: 200, maxHeight: 200, border: '1px solid #ccc' }}
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: -10, right: -10 }}
                        onClick={handleClearLogo}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Grid>

              {logoFile && (
                <Grid item xs={12}>
                  <Typography gutterBottom>Taille du logo : {formData.logo_size}%</Typography>
                  <Slider
                    name="logo_size"
                    value={formData.logo_size}
                    onChange={(e, value) => setFormData({ ...formData, logo_size: value as number })}
                    min={10}
                    max={50}
                    marks
                    disabled={isLoading}
                  />
                </Grid>
              )}

              {/* Actions */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => navigate('/qrcodes')} disabled={isLoading}>
                    Annuler
                  </Button>
                  <Button type="submit" variant="contained" disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} /> : 'Créer le code QR'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default QRCodeCreate;
